import React, { useContext, useMemo, useState } from 'react';
import { Button, Empty, List, Modal, Skeleton, Typography } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import ActionTypes from '@utils/deviceActions';
import SessionContext from '@providers/Session/SessionContext';

const { Text } = Typography;

type Role = {
  id: string | number;
  name: string;
  category: string;
};

type RoleId = Role['id'];
type RolesValue = RoleId[] | string;

interface CustomerPermissionsListProps extends ButtonProps {
  action: ActionTypes;
  roles?: RolesValue;
  rolesId?: RolesValue;
}

const CustomerPermissionsList: React.FC<CustomerPermissionsListProps> = ({
  roles,
  rolesId,
  action,
  ...buttonProps
}) => {
  const { t } = useTranslation(['permissions']);
  const { mainCustomerInContext } = useContext(SessionContext);

  const [modalVisible, setModalVisible] = useState(false);

  const { data: availableRoles, isLoading } = useSwr<Role[]>(
    modalVisible && mainCustomerInContext
      ? `/customers/${mainCustomerInContext.id}/roles/`
      : null,
  );

  const selectedRoleIds = useMemo(
    () => parseRoleIds(roles ?? rolesId),
    [roles, rolesId],
  );

  const selectedRoles = useMemo(() => {
    const roleIds = new Set(selectedRoleIds.map(roleId => String(roleId)));

    return availableRoles?.filter(role => roleIds.has(String(role.id))) || [];
  }, [availableRoles, selectedRoleIds]);

  const groupedSelectedRoles = useMemo(
    () =>
      Object.entries(
        selectedRoles.reduce<Record<string, Role[]>>((groups, role) => {
          const category = role.category || 'others';

          return {
            ...groups,
            [category]: [...(groups[category] || []), role],
          };
        }, {}),
      ),
    [selectedRoles],
  );

  return (
    <>
      <Button
        {...buttonProps}
        type="link"
        onClick={event => {
          buttonProps.onClick?.(event);
          setModalVisible(true);
        }}
      >
        {t('permissions.list')}
      </Button>

      {modalVisible && (
        <Modal
          title={t(
            `permissions.${
              Number(action) === ActionTypes.ADDING_PERMISSIONS
                ? 'added'
                : 'removed'
            }`,
          )}
          open={modalVisible}
          footer={null}
          width={500}
          onCancel={() => setModalVisible(false)}
        >
          <Skeleton loading={isLoading} active>
            {groupedSelectedRoles.length > 0 ? (
              groupedSelectedRoles.map(([category, categoryRoles], index) => (
                <div key={category} style={{ marginTop: index > 0 ? 24 : 0 }}>
                  <h3>{t(`${category}.title`, { defaultValue: category })}</h3>
                  <List
                    dataSource={categoryRoles}
                    renderItem={role => (
                      <List.Item>
                        <Text>
                          {t(`${category}.permissions.${role.name}`, {
                            defaultValue: role.name,
                          })}
                        </Text>
                      </List.Item>
                    )}
                  />
                </div>
              ))
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Skeleton>
        </Modal>
      )}
    </>
  );
};

function parseRoleIds(value?: RolesValue): RoleId[] {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  try {
    const parsedValue = JSON.parse(value);

    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
}

export default CustomerPermissionsList;
